import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const NAV_LINKS = [
    { labelKey: "nav.home", path: "/" },
    { labelKey: "nav.about", path: "/about" },
    { labelKey: "nav.services", path: "/services" },
    { labelKey: "nav.news", path: "/news" },
    { labelKey: "nav.partnerAccess", path: "/partner-access" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        transparent
          ? "bg-transparent"
          : "bg-white border-b border-cool-gray-200"
      }`}
    >
      <nav className="px-6 md:px-12 py-0 flex items-center justify-between h-16 md:h-18">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/d916de51-3f57-462f-a700-620facc3e09c/96d60ca8-e1eb-45a0-b27f-a662d0ca5b1e_EU-LOGO-W1.png?v=85a6b8a22d2db99c2b056706e2aee0e2"
            alt="EU Next Grants Logo"
            className="h-14 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => {
            const active = location.pathname === link.path;
            const isPartner = link.path === "/partner-registration";
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap ${
                  isPartner
                    ? transparent
                      ? "text-gold/90 hover:text-gold"
                      : "text-gold hover:text-gold-dark"
                    : transparent
                    ? active
                      ? "text-gold"
                      : "text-white/90 hover:text-white"
                    : active
                    ? "text-gold"
                    : "text-navy hover:text-navy-light"
                }`}
              >
                {t(link.labelKey)}
              </Link>
            );
          })}

          <LanguageSwitcher transparent={transparent} />

          <Link
            to="/partner-registration"
            className={`ml-2 px-5 py-2 text-sm font-semibold rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
              transparent
                ? "bg-white/15 text-white border border-white/40 hover:bg-white/25"
                : "bg-navy text-white hover:bg-navy-light"
            }`}
          >
            {t("nav.cta")}
          </Link>
        </div>

        {/* Mobile: language switcher + hamburger */}
        <div className="md:hidden flex items-center gap-3">
          <LanguageSwitcher transparent={transparent} />
          <button
            className="flex items-center justify-center w-9 h-9 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i
              className={`${menuOpen ? "ri-close-line" : "ri-menu-3-line"} text-2xl ${
                transparent ? "text-white" : "text-navy"
              }`}
            />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-cool-gray-200 px-6 py-4 flex flex-col gap-4">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-navy font-medium py-1 text-sm"
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <Link
            to="/partner-registration"
            className="mt-2 px-5 py-2 bg-navy text-white text-sm font-semibold rounded-md text-center whitespace-nowrap cursor-pointer"
          >
            {t("nav.cta")}
          </Link>
        </div>
      )}
    </header>
  );
}
